import { fetchClients } from '../api/client'
import { createMortgageHelper, fetchMortgages, updateMortgages } from '../api/mortgage'
import { addProperty, fetchProperties, fetchProperty, updateProperty } from '../api/property'
import { SummaryLoader } from '../api/summary'
import { fetchMortgagesForPropertiesCP } from '../api_client_portal/mortgage'
import { addPropertyCp, fetchClientProperties, fetchPropertyCP, updatePropertyCP } from '../api_client_portal/property'
import { Resolvers } from '../generated/resolvers'
import { mortgageLoader } from '../loaders/mortgage'
import { inactiveCaseStatuses } from '../utils/inactiveCaseStatuses'
import { formatMortgage } from '../utils/schemaMapping/mortgage'
import { fetchVersionedCaseChildEntities } from '../utils/versionedCaseChildEntities'

const PropertyResolver: Resolvers = {
  Query: {
    property: (_parent, { id, version, show_history }) => fetchProperty(id, version, show_history),
    propertyCp: (_parent, { id }) => fetchPropertyCP(id),
    properties: () => fetchProperties(),
    propertiesCp: (_parent, { id }) => fetchClientProperties(id!),
  },
  Property: {
    mortgages: async (parent) => {
      const { case_status, transition_mortgage_versions, property_id, mortgages: parentMortgages } = parent

      // For active cases, we can use the parent mortgages directly, these are resolved via the summary endpoint
      if (case_status && !inactiveCaseStatuses.includes(case_status)) {
        return parentMortgages
      }

      // Keeping the existing logic for completed and NPW cases 
      // They require fetching versioned entities which is not supported by the summary endpoint
      const mortgages =
        case_status &&
        transition_mortgage_versions &&
        (await fetchVersionedCaseChildEntities('mortgage', {
          status: case_status,
          transitionVersions: transition_mortgage_versions,
        }))

      if (mortgages) {
        return mortgages.filter((mortgage) => mortgage.property_secured_ids?.includes(property_id))
      } else {
        return fetchMortgages({ filter_property_secured_id: property_id })
      }
    },
    mortgagesCp: async (parent) => {
      const { property_id } = parent
      const data = await fetchMortgagesForPropertiesCP(property_id)
      const mortgages = data?.mortgages?.map((mortgage) => formatMortgage(mortgage)) || []
      return mortgages
    },
    registered_owners: async (parent) => {
      const { registered_owners_details } = parent

      if (!registered_owners_details) {
        return null
      }

      const clients = await fetchClients({
        client_ids: registered_owners_details,
        client_details: true,
      })

      return clients
    },
  },
  Mutation: {
    addProperty: (_parent, { input }) => addProperty(input),
    addPropertyCp: (_parent, { input }) => addPropertyCp(input),
    updateProperty: (_parent, { propertyId, input }) => updateProperty(input, propertyId),
    updatePropertyCp: (_parent, { propertyId, input }) => updatePropertyCP(input, propertyId),
    mutatePropertyAndMortgages: async (_parent, { input }) => {
      const { propertyId, propertyInput, updateMortgageInput, createMortgageInput } = input

      const property = propertyInput && propertyId ? await updateProperty(propertyInput, propertyId) : undefined

      const updatedMortgages = updateMortgageInput ? await updateMortgages(updateMortgageInput) : undefined

      const createdMortgage = createMortgageInput ? await createMortgageHelper(createMortgageInput) : undefined

      if (createMortgageInput || updateMortgageInput) {
        mortgageLoader.clearAll()
      }

      // Clear summary loader as it will contain stale data
      SummaryLoader.clearAll()

      return { property, createdMortgage, updatedMortgages }
    },
  },
}

export default PropertyResolver
